import { Timestamp } from "@firebase/firestore";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { PropertyReportStatus } from "@stratify/shared";
import { format } from "date-fns";
import React from "react";
import { useSelector } from "react-redux";
import { selectUserId } from "../../auth/store/authSelectors";
import { usePropertyReportTask } from "../hooks/usePropertyReportTask";

interface PropertyReportStatusIndicatorProps {
  propertyId: string;
}

export const PropertyReportStatusIndicator: React.FC<
  PropertyReportStatusIndicatorProps
> = ({ propertyId }) => {
  const userId = useSelector(selectUserId);

  if (!userId) {
    return null;
  }

  const {
    reportTask,
    isLoading,
    error: hookError,
  } = usePropertyReportTask(userId, propertyId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (hookError) {
    return (
      <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
        <AlertTitle>Error</AlertTitle>
        {hookError}
      </Alert>
    );
  }

  if (!reportTask) {
    console.log("No report task found for property:", propertyId);
    return null;
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    return format(new Date(date), "PPp"); // e.g., "Apr 29, 2023, 3:00 PM"
  };

  const getLastUpdatedTime = () => {
    const dates = [
      reportTask.completedAt,
      reportTask.startedAt,
      reportTask.createdAt,
    ].filter(Boolean);

    const latestTimestamp: Timestamp | undefined = dates[0];
    if (!latestTimestamp) {
      return "";
    }

    return dates.length > 0 ? formatDate(latestTimestamp.toDate()) : "";
  };

  let severity: "info" | "success" | "error" = "info";
  let title = "";
  let message = "";
  let showSpinner = false;

  switch (reportTask.status) {
    case PropertyReportStatus.PENDING_DOCUMENTS:
      severity = "info";
      title = "Waiting for Documents";
      message =
        "Waiting for all property documents to be processed before generating the report.";
      showSpinner = true;
      break;
    case PropertyReportStatus.READY_FOR_GENERATION:
      severity = "info";
      title = "Ready to Generate";
      message = "All documents processed, ready to generate the report.";
      break;
    case PropertyReportStatus.GENERATING:
      severity = "info";
      title = "Generating Report";
      message = "Report generation is in progress.";
      showSpinner = true;
      break;
    case PropertyReportStatus.COMPLETED:
      severity = "success";
      title = "Report Ready";
      message = "";
      break;
    case PropertyReportStatus.FAILED:
      severity = "error";
      title = "Report Generation Failed";
      message =
        reportTask.error || "An error occurred while generating the report.";
      break;
    default:
      return null;
  }

  const lastUpdated = getLastUpdatedTime();
  if (lastUpdated) {
    message += ` Last updated: ${lastUpdated}`;
  }

  return (
    <Alert severity={severity} sx={{ mt: 0, mb: 0, mx: 1 }}>
      <AlertTitle>{title}</AlertTitle>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {showSpinner && <CircularProgress size={20} sx={{ mr: 1 }} />}
        <Typography variant="body2">{message}</Typography>
      </Box>
    </Alert>
  );
};
